<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-if="showPrompt"
        class="install-prompt"
        role="alert"
        aria-live="polite"
      >
        <div class="prompt-content">
          <div class="prompt-icon">
            <i class="bi bi-phone" aria-hidden="true"></i>
          </div>
          <div class="prompt-text">
            <strong>Add to Home Screen</strong>
            <span>Install the app for quick access</span>
          </div>
        </div>
        <div class="prompt-actions">
          <button
            class="btn-dismiss"
            @click="dismissPrompt"
            aria-label="Dismiss install prompt"
          >
            Later
          </button>
          <router-link
            to="/install-app"
            class="btn-install"
            @click="handleInstallClick"
          >
            <i class="bi bi-download" aria-hidden="true"></i>
            Install
          </router-link>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, inject } from 'vue';

const STORAGE_KEY = 'install-prompt-dismissed';
const SESSIONS_KEY = 'install-prompt-sessions';
const DISMISS_DURATION_DAYS = 7; // Show again after 7 days
const MAX_PROMPT_SESSIONS = 3;   // Only offer during the first N sessions

const isMobile = inject('isMobile', ref(false));
const promptDismissed = ref(false);

// Check if running as standalone PWA
const isStandalone = computed(() => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
});

// Only offer the prompt during a user's first few sessions
const withinSessionBudget = ref(true);

// Show prompt only on mobile, not in standalone mode, not dismissed,
// and only during the first few sessions
const showPrompt = computed(() => {
  return isMobile.value && !isStandalone.value && !promptDismissed.value && withinSessionBudget.value;
});

// Reserve layout space while the prompt is visible so it never covers
// page content (bottom rows, action bars).
watch(showPrompt, (visible) => {
  document.body.classList.toggle('has-install-prompt', visible);
}, { immediate: true });

onUnmounted(() => {
  document.body.classList.remove('has-install-prompt');
});

function dismissPrompt() {
  promptDismissed.value = true;
  // Save dismissal with timestamp
  const dismissData = {
    dismissed: true,
    timestamp: Date.now()
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissData));
}

function handleInstallClick() {
  // Dismiss prompt when user clicks install
  promptDismissed.value = true;
}

function checkDismissalStatus() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      promptDismissed.value = false;
      return;
    }

    const data = JSON.parse(stored);
    const daysSinceDismiss = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);

    // Show again after DISMISS_DURATION_DAYS
    if (daysSinceDismiss > DISMISS_DURATION_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      promptDismissed.value = false;
    } else {
      promptDismissed.value = true;
    }
  } catch {
    promptDismissed.value = false;
  }
}

onMounted(() => {
  checkDismissalStatus();
  // Count sessions; stop offering the prompt after the first few.
  try {
    const sessions = parseInt(localStorage.getItem(SESSIONS_KEY) || '0', 10) + 1;
    localStorage.setItem(SESSIONS_KEY, String(sessions));
    withinSessionBudget.value = sessions <= MAX_PROMPT_SESSIONS;
  } catch {
    withinSessionBudget.value = true;
  }
});
</script>

<style>
/* Unscoped on purpose: reserve space at the bottom of the scroll container
   while the install prompt is visible, so it never covers page content. */
body.has-install-prompt .main-content {
  padding-bottom: calc(96px + env(safe-area-inset-bottom));
}
</style>

<style scoped>
.install-prompt {
  position: fixed;
  bottom: calc(4.5rem + env(safe-area-inset-bottom));
  left: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #212529, #343a40);
  color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  z-index: 10000;
}

.prompt-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.prompt-icon {
  width: 40px;
  height: 40px;
  background: rgba(123, 143, 212, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.prompt-icon i {
  font-size: 1.25rem;
  color: #7B8FD4;
}

.prompt-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.prompt-text strong {
  font-size: 0.9375rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.prompt-text span {
  font-size: 0.8125rem;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.prompt-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-dismiss {
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-dismiss:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-install {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: linear-gradient(135deg, #7B8FD4, #5a6fb8);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-install:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(123, 143, 212, 0.4);
  color: white;
}

.btn-install i {
  font-size: 0.875rem;
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Mobile small screens */
@media (max-width: 380px) {
  .install-prompt {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .prompt-actions {
    justify-content: flex-end;
  }

  .btn-dismiss,
  .btn-install {
    padding: 0.625rem 1rem;
    min-height: 44px;
  }
}
</style>
